import React, { useEffect, useMemo, useState } from 'react';
import { Check, KeyRound, Pencil, Plus, RotateCcw, Save, Search, ShieldCheck, ToggleLeft, ToggleRight, Trash2, X } from 'lucide-react';

import api, { getApiErrorMessage } from '../../services/api';
import { FeedbackBanner, LoadingState, PageHeader, PageLayout } from '../../components/ui/PageLayout';
import { MODULE_LABELS } from '../../utils/permissions';

const EMPTY_FORM = {
  nome: '',
  email: '',
  login: '',
  senha_provisoria: '',
  perfil: 'porteiro',
  ativo: true,
  trocar_senha_primeiro_acesso: true,
  condominio_id: '',
  permissoes: [],
};

const VIEW_ONLY = new Set(['inicio', 'cameras', 'logs']);

function buildPermissions(modulos, base = {}) {
  return modulos.map((modulo) => ({
    modulo,
    visualizar: Boolean(base[modulo]?.visualizar),
    criar: !VIEW_ONLY.has(modulo) && Boolean(base[modulo]?.criar),
    editar: !VIEW_ONLY.has(modulo) && Boolean(base[modulo]?.editar),
    excluir: !VIEW_ONLY.has(modulo) && Boolean(base[modulo]?.excluir),
  }));
}

function UsuariosSistemaPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [meta, setMeta] = useState({ modulos: [], perfis: {}, permissoes_por_perfil: {} });
  const [condominios, setCondominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busca, setBusca] = useState('');
  const [perfilFiltro, setPerfilFiltro] = useState('todos');
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetSenha, setResetSenha] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [metaResp, condominiosResp, usuariosResp] = await Promise.all([
        api.get('/usuarios-sistema/meta'),
        api.get('/usuarios-sistema/condominios'),
        api.get('/usuarios-sistema/'),
      ]);
      setMeta(metaResp.data);
      setCondominios(condominiosResp.data);
      setUsuarios(usuariosResp.data);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Não foi possível carregar usuários.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const usuariosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return usuarios.filter((usuario) => {
      const matchBusca = !termo || [usuario.nome, usuario.email, usuario.login, usuario.condominio_nome].some((valor) => (valor || '').toLowerCase().includes(termo));
      const matchPerfil = perfilFiltro === 'todos' || usuario.perfil === perfilFiltro;
      const matchStatus = statusFiltro === 'todos' || (statusFiltro === 'ativos' && usuario.ativo) || (statusFiltro === 'inativos' && !usuario.ativo);
      return matchBusca && matchPerfil && matchStatus;
    });
  }, [usuarios, busca, perfilFiltro, statusFiltro]);

  const openNew = () => {
    const perfil = 'porteiro';
    setEditingUser(null);
    setForm({
      ...EMPTY_FORM,
      perfil,
      condominio_id: condominios[0]?.id || '',
      permissoes: buildPermissions(meta.modulos, meta.permissoes_por_perfil?.[perfil]),
    });
    setModalOpen(true);
  };

  const openEdit = (usuario) => {
    setEditingUser(usuario);
    setForm({
      nome: usuario.nome || '',
      email: usuario.email || '',
      login: usuario.login || '',
      senha_provisoria: '',
      perfil: usuario.perfil || 'porteiro',
      ativo: Boolean(usuario.ativo),
      trocar_senha_primeiro_acesso: Boolean(usuario.trocar_senha_primeiro_acesso),
      condominio_id: usuario.condominio_id || '',
      permissoes: buildPermissions(meta.modulos, Object.fromEntries((usuario.permissoes || []).map((item) => [item.modulo, item]))),
    });
    setModalOpen(true);
  };

  const updatePermission = (modulo, acao, value) => {
    setForm((current) => ({
      ...current,
      permissoes: current.permissoes.map((item) => {
        if (item.modulo !== modulo) return item;
        if (acao === 'visualizar' && !value) return { ...item, visualizar: false, criar: false, editar: false, excluir: false };
        return { ...item, [acao]: value, visualizar: acao === 'visualizar' ? value : value || item.visualizar };
      }),
    }));
  };

  const markAll = (value) => {
    setForm((current) => ({
      ...current,
      permissoes: current.permissoes.map((item) => ({ ...item, visualizar: value, criar: value && !VIEW_ONLY.has(item.modulo), editar: value && !VIEW_ONLY.has(item.modulo), excluir: value && !VIEW_ONLY.has(item.modulo) })),
    }));
  };

  const applyProfile = () => {
    setForm((current) => ({
      ...current,
      permissoes: buildPermissions(meta.modulos, meta.permissoes_por_perfil?.[current.perfil]),
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...form,
        condominio_id: form.condominio_id ? Number(form.condominio_id) : null,
      };
      if (editingUser && !payload.senha_provisoria) {
        delete payload.senha_provisoria;
      }
      if (editingUser) {
        await api.put(`/usuarios-sistema/${editingUser.id}`, payload);
      } else {
        await api.post('/usuarios-sistema/', payload);
      }
      setMessage('Usuário salvo com sucesso.');
      setModalOpen(false);
      await fetchData();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Você não possui permissão para realizar esta ação.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (usuario) => {
    try {
      await api.patch(`/usuarios-sistema/${usuario.id}/status`);
      await fetchData();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Você não possui permissão para realizar esta ação.'));
    }
  };

  const openResetPassword = (usuario) => {
    setResetUser(usuario);
    setResetSenha('');
    setResetConfirm('');
    setResetError('');
    setMessage('');
    setError('');
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    if (resetSaving) return;
    if (resetSenha.length < 6) {
      setResetError('A senha provisória deve ter pelo menos 6 caracteres.');
      return;
    }
    if (resetSenha !== resetConfirm) {
      setResetError('A confirmação da senha não confere.');
      return;
    }
    setResetSaving(true);
    setResetError('');
    try {
      await api.post(`/usuarios-sistema/${resetUser.id}/resetar-senha`, { senha_provisoria: resetSenha, trocar_senha_primeiro_acesso: true });
      setResetUser(null);
      setResetSenha('');
      setResetConfirm('');
      setMessage('Senha redefinida com sucesso.');
    } catch (requestError) {
      setResetError(getApiErrorMessage(requestError, 'Não foi possível redefinir a senha.'));
    } finally { setResetSaving(false); }
  };

  const deleteUser = async (usuario) => {
    if (!window.confirm(`Excluir definitivamente ${usuario.nome}? Esta ação não pode ser desfeita.`)) return;
    try {
      setError('');
      setMessage('');
      const response = await api.delete(`/usuarios-sistema/${usuario.id}`);
      setMessage(response.data?.message || 'Usuário excluído com sucesso.');
      await fetchData();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Você não possui permissão para realizar esta ação.'));
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Usuários do Sistema"
        description="Controle de usuários administrativos, perfis e permissões por módulo."
        actions={<button type="button" onClick={openNew} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg bg-condo-dark px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-slate-700 disabled:opacity-50"><Plus size={18} /> Novo usuário</button>}
      />

      <FeedbackBanner type="success" message={message} onClose={() => setMessage('')} />
      <FeedbackBanner type="error" message={error} onClose={() => setError('')} />

      <section className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm lg:grid-cols-[1fr_220px_180px]">
        <label className="relative block">
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
          <input value={busca} onChange={(event) => setBusca(event.target.value)} placeholder="Buscar por nome, login, e-mail ou condomínio" className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal" />
        </label>
        <select value={perfilFiltro} onChange={(event) => setPerfilFiltro(event.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal">
          <option value="todos">Todos os perfis</option>
          {Object.entries(meta.perfis || {}).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select value={statusFiltro} onChange={(event) => setStatusFiltro(event.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal">
          <option value="todos">Todos</option>
          <option value="ativos">Ativos</option>
          <option value="inativos">Inativos</option>
        </select>
      </section>

      {loading ? <LoadingState /> : (
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-500">
                <tr><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Login / e-mail</th><th className="px-4 py-3">Perfil</th><th className="px-4 py-3">Condomínio</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Último acesso</th><th className="px-4 py-3 text-right">Ações</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {usuariosFiltrados.map((usuario) => (
                  <tr key={usuario.id} className="text-slate-700">
                    <td className="px-4 py-3 font-semibold text-slate-900">{usuario.nome}</td>
                    <td className="px-4 py-3"><span className="block font-medium">{usuario.login}</span><span className="text-xs text-slate-500">{usuario.email}</span></td>
                    <td className="px-4 py-3">{meta.perfis?.[usuario.perfil] || usuario.perfil}</td>
                    <td className="px-4 py-3">{usuario.condominio_nome || '-'}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${usuario.ativo ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{usuario.ativo ? 'Ativo' : 'Inativo'}</span></td>
                    <td className="px-4 py-3">{usuario.ultimo_acesso ? new Date(usuario.ultimo_acesso).toLocaleString('pt-BR') : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button title="Editar" onClick={() => openEdit(usuario)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil size={17} /></button>
                        <button title={usuario.ativo ? 'Inativar' : 'Ativar'} onClick={() => toggleStatus(usuario)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">{usuario.ativo ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}</button>
                        <button title="Redefinir senha" onClick={() => openResetPassword(usuario)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><KeyRound size={17} /></button>
                        <button title="Excluir" onClick={() => deleteUser(usuario)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 size={17} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!usuariosFiltrados.length ? <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">Nenhum usuário encontrado.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 px-4 py-8">
          <form onSubmit={handleSave} role="dialog" aria-modal="true" aria-label={editingUser ? 'Editar usuário' : 'Novo usuário'} className="w-full max-w-5xl rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800"><ShieldCheck size={22} /> {editingUser ? 'Editar usuário' : 'Novo usuário'}</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Input label="Nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} required />
              <Input label="E-mail" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Input label="Login" value={form.login} onChange={(v) => setForm({ ...form, login: v })} required />
              <Input label="Senha provisória" type="password" value={form.senha_provisoria} onChange={(v) => setForm({ ...form, senha_provisoria: v })} required={!editingUser} />
              <label className="block"><span className="mb-2 block text-sm font-semibold text-slate-700">Perfil</span><select value={form.perfil} onChange={(event) => setForm({ ...form, perfil: event.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal">{Object.entries(meta.perfis || {}).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label className="block"><span className="mb-2 block text-sm font-semibold text-slate-700">Condomínio</span><select value={form.condominio_id} onChange={(event) => setForm({ ...form, condominio_id: event.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal">{condominios.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}</select></label>
              <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={form.ativo} onChange={(event) => setForm({ ...form, ativo: event.target.checked })} /> Ativo</label>
              <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-slate-700 lg:col-span-2"><input type="checkbox" checked={form.trocar_senha_primeiro_acesso} onChange={(event) => setForm({ ...form, trocar_senha_primeiro_acesso: event.target.checked })} /> Obrigar troca de senha no primeiro acesso</label>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button type="button" onClick={() => markAll(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Check size={16} /> Marcar todas</button>
              <button type="button" onClick={() => markAll(false)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><X size={16} /> Desmarcar todas</button>
              <button type="button" onClick={() => setForm((current) => ({ ...current, permissoes: current.permissoes.map((item) => ({ ...item, visualizar: true, criar: false, editar: false, excluir: false })) }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Somente visualizar</button>
              <button type="button" onClick={applyProfile} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><RotateCcw size={16} /> Aplicar permissões do perfil</button>
            </div>

            <h3 className="mt-5 font-semibold text-slate-800">Acesso às abas</h3>
            <p className="mt-1 text-sm text-slate-500">Visualizar libera a aba no menu. Criar, editar e excluir definem as operações permitidas. Ao ocultar uma aba, suas operações também são desmarcadas.</p>

            <div className="mt-4 overflow-x-auto rounded-xl border border-gray-100">
              <table className="min-w-full text-sm"><thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-500"><tr><th className="px-4 py-3">Aba do sistema</th><th className="px-4 py-3 text-center">Visualizar</th><th className="px-4 py-3 text-center">Criar</th><th className="px-4 py-3 text-center">Editar</th><th className="px-4 py-3 text-center">Excluir</th></tr></thead><tbody className="divide-y divide-gray-100">{form.permissoes.map((item) => {
                const label = MODULE_LABELS[item.modulo] || meta.nomes_modulos?.[item.modulo] || item.modulo;
                return <tr key={item.modulo}><td className="px-4 py-3 font-semibold text-slate-700">{label}</td>{['visualizar', 'criar', 'editar', 'excluir'].map((acao) => <td key={acao} className="px-4 py-3 text-center">{VIEW_ONLY.has(item.modulo) && acao !== 'visualizar' ? <span className="text-slate-300" title="Aba de consulta">—</span> : <input type="checkbox" aria-label={`${label}: ${acao}`} checked={Boolean(item[acao])} onChange={(event) => updatePermission(item.modulo, acao, event.target.checked)} />}</td>)}</tr>;
              })}
                <tr><td className="px-4 py-3 font-semibold text-slate-700">Backup</td><td colSpan={4} className="px-4 py-3 text-sm text-slate-500">Exclusivo do Super Administrador</td></tr>
                {/* developer-only:start */}
                <tr><td className="px-4 py-3 font-semibold text-slate-700">Developer</td><td colSpan={4} className="px-4 py-3 text-sm text-slate-500">Exclusivo do Super Administrador</td></tr>
                {/* developer-only:end */}
              </tbody></table>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-condo-dark px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"><Save size={17} /> {saving ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </form>
        </div>
      ) : null}
      {resetUser && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
        <form onSubmit={resetPassword} role="dialog" aria-modal="true" aria-labelledby="reset-password-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
          <h2 id="reset-password-title" className="text-xl font-semibold text-slate-800">Redefinir senha</h2>
          <p className="mt-2 text-sm text-slate-500">Defina a senha provisória de {resetUser.nome}. O usuário precisará trocá-la no próximo acesso.</p>
          <div className="mt-4 space-y-4">
            <label className="block text-sm font-semibold text-slate-700" htmlFor="reset-password">Nova senha provisória</label>
            <input id="reset-password" type="password" autoComplete="new-password" autoFocus minLength={6} required value={resetSenha} onChange={(event) => setResetSenha(event.target.value)} disabled={resetSaving} className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-slate-800 outline-none focus:ring-2 focus:ring-condo-teal" />
            <p className="text-xs text-slate-500">Use pelo menos 6 caracteres.</p>
            <label className="block text-sm font-semibold text-slate-700" htmlFor="reset-password-confirm">Confirmar senha provisória</label>
            <input id="reset-password-confirm" type="password" autoComplete="new-password" required value={resetConfirm} onChange={(event) => setResetConfirm(event.target.value)} disabled={resetSaving} className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-slate-800 outline-none focus:ring-2 focus:ring-condo-teal" />
            <FeedbackBanner type="error" message={resetError} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => { setResetUser(null); setResetSenha(''); setResetConfirm(''); }} disabled={resetSaving} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Cancelar</button>
            <button type="submit" disabled={resetSaving} className="rounded-xl bg-condo-dark px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{resetSaving ? 'Redefinindo…' : 'Redefinir senha'}</button>
          </div>
        </form>
      </div>}
    </PageLayout>
  );
}

function Input({ label, value, onChange, type = 'text', required = false }) {
  return <label className="block"><span className="mb-2 block text-sm font-semibold text-slate-700">{label}</span><input type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal" /></label>;
}

export default UsuariosSistemaPage;
